import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentArrowDownIcon, FunnelIcon } from '@heroicons/react/24/outline'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const exportColumns = [
  { key: 'stickerId', label: 'Sticker ID' }, { key: 'stickerType', label: 'Type' },
  { key: 'workerName', label: 'Worker' }, { key: 'date', label: 'Date' },
  { key: 'containerName', label: 'Container' }, { key: 'containerMaterial', label: 'Material' },
  { key: 'grossWeight', label: 'Gross Weight' }
]

export default function PackingDataEdit () {
  const { user } = useAuth()
  const { t } = useTranslation()
  const hasFetched = useRef(false)
  const currentFilter = useRef(null)
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)
  const [state, setState] = useState({
    packingData: [],
    searchTerm: '',
    loading: true
  })

  const getPackingData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/packing-data/${user.farmId}?${buildDashboardDateRangeQuery(dateRange)}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, packingData: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    const nextFilter = JSON.stringify(dateRange)
    if (hasFetched.current && currentFilter.current === nextFilter) return
    hasFetched.current = true
    currentFilter.current = nextFilter
    getPackingData()
  }, [user?.farmId, dateRange])

  const filteredPackingData = filterRowsBySearch(state.packingData, state.searchTerm)

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.packingDataEdit.loading')} rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>{getDateRangeSummary(dateRange)}</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditExcel({ rows: filteredPackingData, columns: exportColumns, fileName: `packing-data-${currentDate}`, sheetName: 'Packing Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('common.exportExcel')}</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditPdf({ rows: filteredPackingData, columns: exportColumns, fileName: `packing-data-${currentDate}`, title: 'Packing Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('mgmt.common.exportPdf')}</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder={t('mgmt.dataEdit.search')}
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent className='flex items-center gap-2' onClick={() => setShowFilters(true)}><FunnelIcon className='w-5 h-5' /><span>{t('dashboard.advancedFilters')}</span></ButtonComponent>
      </div>

      <div className='data-table'>
        <TableComponent
          tableData={{
            columns: [
              { key: 'stickerId', labelKey: 'mgmt.dataEdit.cols.stickerId' },
              { key: 'stickerType', labelKey: 'mgmt.dataEdit.cols.type' },
              { key: 'workerName', labelKey: 'mgmt.dataEdit.cols.worker' },
              { key: 'date', labelKey: 'mgmt.dataEdit.cols.date', cell: (value) => parseDate(value) },
              { key: 'containerName', labelKey: 'mgmt.dataEdit.cols.container' },
              { key: 'containerMaterial', labelKey: 'mgmt.dataEdit.cols.material' },
              { key: 'grossWeight', labelKey: 'mgmt.dataEdit.cols.grossWeight' }
            ],
            rows: filteredPackingData
          }}
          defaultSortColumn='date'
        />
      </div>
      <DateRangeFilterModal isOpen={showFilters} dateRange={dateRange} sectors={filterOptions.sectors} farmingBlocks={filterOptions.farmingBlocks} groups={filterOptions.groups} activities={filterOptions.activities} maxDate={currentDate} onClose={() => setShowFilters(false)} onApply={(nextRange) => { setDateRange(nextRange); setShowFilters(false) }} />
    </div>
  )
}
